import { DebugLogger } from '@affine/debug';

import { BackendError, NetworkError } from './error';
import type { FetchInit } from './types';
import { getBaseUrl } from './utils';

const logger = new DebugLogger('dnb:fetch');

const getTimeout = (
  timeout?: number,
  abortController = new AbortController()
) => {
  const existingTimeout = !Number.isSafeInteger(timeout);

  const _timeout = timeout ?? 15000;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const set = () => {
    if (!existingTimeout) return;

    timeoutId = setTimeout(() => {
      abortController.abort('timeout');
    }, _timeout);
  };

  const clear = () => {
    if (!existingTimeout || !timeoutId) return;

    clearTimeout(timeoutId);
  };

  return { clear, set };
};

export const fetcher = async (
  input: string,
  init?: FetchInit
): Promise<Response> => {
  logger.debug('fetch', input);
  const externalSignal = init?.signal;
  if (externalSignal?.aborted) {
    throw externalSignal.reason;
  }
  const abortController = new AbortController();
  externalSignal?.addEventListener('abort', reason => {
    abortController.abort(reason);
  });

  const { clear, set } = getTimeout(init?.timeout);
  const isApiV2 = input.includes('/api/v2/');

  set();

  const res = await fetch(new URL(input, getBaseUrl()), {
    ...init,
    signal: abortController.signal,
  }).catch(err => {
    throw new NetworkError(err);
  });

  clear();

  if (res.status === 504) {
    const error = new Error('Gateway Timeout');
    logger.debug('network error', error);
    throw new NetworkError(error, res.status);
  }
  if (!res.ok) {
    logger.warn('backend error', new Error(`${res.status} ${res.statusText}`));
    let reason: string | any = '';
    if (res.headers.get('Content-Type')?.includes('application/json')) {
      try {
        reason = await res.json();
      } catch {
        // ignore
      }
    }
    if (isApiV2) {
      throw reason;
    }
    throw new BackendError(reason, res.status);
  }
  return res;
};
